import React, { useState } from 'react';
import { Group, Text, Rect, Path } from 'react-konva';
import { Html } from 'react-konva-utils';
import { Slice } from '../../modeling';
// import { default as validationService } from '../../modeling/domain/validation';
import { setCanvasCursor } from '../../canvas/domain/cursorUtils';

interface ChapterGroupProps {
    chapterName: string;
    slices: Slice[];
    sliceBounds: Map<string, { x: number, y: number, width: number, height: number, minX: number, maxX: number, minY: number, maxY: number }>;
    onRenameChapter?: (sub: string, newName: string) => void;
}

const ChapterGroup: React.FC<ChapterGroupProps> = ({
    chapterName,
    slices,
    sliceBounds,
    onRenameChapter
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(chapterName);

    // 1. Calculate Chapter Bounds union of all slice VISUAL bounds
    // We wrap the already-padded slice containers.
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let hasValidBounds = false;

    slices.forEach(slice => {
        const bounds = sliceBounds.get(slice.id);
        if (bounds) {
            hasValidBounds = true;
            minX = Math.min(minX, bounds.x);
            maxX = Math.max(maxX, bounds.x + bounds.width);
            minY = Math.min(minY, bounds.y);
            maxY = Math.max(maxY, bounds.y + bounds.height);
        }
    });

    if (!hasValidBounds) return null;

    // Nesting Buffer: Distance between Slice boundary and Chapter boundary
    const NESTING_BUFFER = 40;
    minX -= NESTING_BUFFER;
    minY -= NESTING_BUFFER;
    maxX += NESTING_BUFFER;
    maxY += NESTING_BUFFER;

    const width = maxX - minX;
    const height = maxY - minY;

    const handleRenameSubmit = () => {
        if (editName.trim() && editName !== chapterName && onRenameChapter) {
            onRenameChapter(chapterName, editName.trim());
        }
        setIsEditing(false);
    };

    const textWidthEstimate = (chapterName || '').length * 8; // Approx width

    return (
        <Group
            onMouseEnter={(e) => {
                setIsHovered(true);
                setCanvasCursor(e, 'pointer');
            }}
            onMouseLeave={(e) => {
                setIsHovered(false);
                setCanvasCursor(e, '');
            }}
        >
            {/* Dashed "Stapled" Border - Chapters are the GROUPS */}
            <Rect
                x={minX}
                y={minY}
                width={width}
                height={height}
                stroke="#475569" // Slate 600
                strokeWidth={2}
                dash={[10, 5]} // Classic "Stapled" Dashed look
                cornerRadius={16}
                opacity={0.8}
                listening={false}
            />

            {/* Header Label - Centered like SliceGroup */}
            {!isEditing ? (
                <Group
                    x={minX}
                    y={minY - 40}
                    width={width}
                    onClick={() => setIsEditing(true)}
                >
                    <Text
                        x={0}
                        y={0}
                        width={width}
                        text={chapterName.toUpperCase()}
                        fontSize={16}
                        fontStyle="bold"
                        fill="#475569" // Slate 600
                        align="center"
                    />
                    {/* Pen Icon - Conditioned on Hover or always visible? Slices show always. */}
                    <Group 
                        x={(width / 2) + (textWidthEstimate / 2) + 15} 
                        y={-4} 
                        scale={{ x: 0.75, y: 0.75 }} 
                        opacity={isHovered ? 1 : 0.4}
                    >
                        <Path data="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" stroke={isHovered ? "#a855f7" : "#475569"} strokeWidth={2.5} lineCap="round" lineJoin="round" fillEnabled={false} />
                        <Path data="m15 5 4 4" stroke={isHovered ? "#a855f7" : "#475569"} strokeWidth={2.5} lineCap="round" lineJoin="round" fillEnabled={false} />
                    </Group>
                </Group>
            ) : (
                <Html divProps={{ style: { pointerEvents: 'auto' } }}>
                    <div className="absolute" style={{
                        left: `${minX + (width / 2) - 100}px`,
                        top: `${minY - 35}px`
                    }}>
                        <input
                            autoFocus
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onBlur={handleRenameSubmit}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleRenameSubmit();
                                if (e.key === 'Escape') setIsEditing(false);
                            }}
                            className="bg-white border-2 border-slate-500 rounded px-2 py-1 text-sm font-bold uppercase text-slate-700 outline-none shadow-lg text-center"
                            style={{ width: '200px' }}
                        />
                    </div>
                </Html>
            )}
        </Group>
    );
};

export default ChapterGroup;
